import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  api, hasSessionHint, rinnovaSeVecchio, impostaImpersonazione, SESSIONE_SCADUTA, ABBONAMENTO_RICHIESTO,
} from "@/lib/api";
import {
  computeTrialSubscriptionState, sessioneRipresaDaChiudere, dataFineAccesso,
} from "@/lib/accessoPiano";
import { ADMIN_PATH } from "@/lib/adminRoutes";

// Nessun token in localStorage: la sessione vive nei cookie httpOnly.
// In localStorage resta solo la cache dell'utente, che non e' un segreto e
// serve a non mostrare lo splash di caricamento a ogni ricarica.

const AuthContext = createContext(null);

const USER_CACHE_KEY = "aurea_user_cache";

/** Read the cached user (if any) from localStorage — used to avoid a full-screen
 *  "Caricamento" splash on every page refresh when the token is still valid. */
const readCachedUser = () => {
  try {
    if (!hasSessionHint()) return null;
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.id) return parsed;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[auth] cached user parse failed:", err?.message);
  }
  return null;
};

const writeCachedUser = (u) => {
  try {
    if (u && typeof u === "object") localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[auth] cache user write failed:", err?.message);
  }
};

export const AuthProvider = ({ children }) => {
  // Seed from cache so the protected layout renders immediately after refresh
  // (no full-screen "Caricamento" flash). The cache is then validated in the
  // background by calling /auth/me; if it 401s we drop the session, but for
  // any transient network/5xx we keep the cached session alive.
  const cached = readCachedUser();
  const [user, setUser] = useState(cached || null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(!cached);
  // Perche' la sessione ripresa dalla cache e' stata chiusa: lo legge la
  // pagina di login, per non mostrare un login muto a chi era gia' dentro.
  const [pianoScaduto, setPianoScaduto] = useState(null);
  // Fix 3: useState lazy initializer viene eseguito UNA SOLA VOLTA al mount —
  // non si aggiorna mai dopo. useMemo su [user] garantisce ricalcolo ad ogni cambio di sessione.
  // isImpersonating = user è un planner (non admin) E il token admin è ancora in localStorage
  // (l'admin ha fatto "Login As" e il suo token è ancora lì come backup).

  const persistUser = useCallback((u) => {
    setUser(u);
    if (u && typeof u === "object") writeCachedUser(u);
    else writeCachedUser(null);
  }, []);

  // Aggiorna subito lo stato locale quando una preferenza dell'account viene
  // salvata in background. Serve a evitare che una pagina appena rimontata
  // legga ancora il profilo precedente dalla memoria dell'app.
  const markTutorialSeen = useCallback(async () => {
    let aggiornato = false;
    setUser((corrente) => {
      if (!corrente || typeof corrente !== "object" || corrente.tutorial_seen) return corrente;
      const prossimo = { ...corrente, tutorial_seen: true };
      writeCachedUser(prossimo);
      aggiornato = true;
      return prossimo;
    });
    try {
      await api.patch("/auth/me", { tutorial_seen: true });
    } catch (err) {
      // Lo stato locale evita riaperture durante questa sessione; il backend
      // verrà ritentato al prossimo avvio del tour esplicito.
      // eslint-disable-next-line no-console
      console.warn("[auth] tutorial_seen update failed:", err?.message);
    }
    return aggiornato;
  }, []);

  const fetchMe = useCallback(async (attempt = 0, opzioni = {}) => {
    const { allaRipresa = false } = opzioni;
    try {
      const { data } = await api.get("/auth/me");
      // Alla ripresa dalla cache un account scaduto non entra: si chiude la
      // sessione e si torna al login, che spiega cosa e' successo.
      // Solo qui, e mai dopo un login vero: se no si avvita in un giro
      // chiuso login -> scaduto -> login. E mai dentro refreshUser, che
      // gira su /billing/success subito dopo il pagamento, quando il
      // webhook puo' non essere ancora arrivato.
      if (allaRipresa && sessioneRipresaDaChiudere(computeTrialSubscriptionState(data), window.location.pathname)) {
        setPianoScaduto({ scadutoIl: dataFineAccesso(data), email: data?.email || null });
        try {
          await api.post("/auth/logout");
        } catch (errLogout) {
          // eslint-disable-next-line no-console
          console.warn("[auth] logout dopo scadenza fallito:", errLogout?.message);
        }
        persistUser(false);
        return;
      }
      persistUser(data);
    } catch (err) {
      const status = err?.response?.status;
      // 401 = real "logged out" → drop the session and the cache.
      // Eccezione: `rinnovoIncerto` lo mette l'interceptor quando il rinnovo
      // non e' riuscito per un guasto di trasporto (5xx, timeout, rete). Li'
      // non si sa se la sessione sia ancora buona, e questo 401 e' solo quello
      // della richiesta originale: trattarlo come logout butterebbe fuori la
      // planner per un backend che sfarfalla. Cade nel ramo dei ritentativi.
      if (status === 401 && !err?.rinnovoIncerto) {
        persistUser(false);
        return;
      }
      // Network glitches and 5xx (e.g. backend cold start in preview) shouldn't
      // log the user out. Retry up to 3 times with a small backoff before
      // surrendering, and keep the existing cached session if a token is still
      // around.
      if (attempt < 3) {
        // eslint-disable-next-line no-console
        console.warn(`[auth] /auth/me transient failure (${status || "net"}), retrying…`);
        await new Promise((r) => setTimeout(r, 600 + attempt * 600));
        return fetchMe(attempt + 1, opzioni);
      }
      // After 3 failed retries, only force logout if there's no token at all
      // — otherwise we leave the user alone and let the next request retry.
      if (!hasSessionHint()) {
        persistUser(false);
      } else {
        // eslint-disable-next-line no-console
        console.warn("[auth] /auth/me unreachable after 3 retries, keeping cached session.");
      }
    } finally {
      setLoading(false);
    }
  }, [persistUser]);

  useEffect(() => {
    // Si interroga il backend solo se la spia dice che una sessione esiste:
    // evita un 401 garantito sulle pagine pubbliche (/login, /register).
    if (!hasSessionHint()) {
      setLoading(false);
      return;
    }
    fetchMe(0, { allaRipresa: true });
  }, [fetchMe]);

  /** Salva il token della sessione appena ottenuta e carica l'utente canonico.
   *  Condiviso fra login normale e secondo passo del login admin. */
  const applySession = useCallback(async (data) => {
    // Niente da salvare: i cookie di sessione li ha gia' impostati il backend
    // nella risposta, e sono httpOnly. `data.access_token` esiste ancora nel
    // corpo per la suite di test, ma il browser lo ignora di proposito.
    // Canonicalizza l'utente via /auth/me: la risposta di /auth/login è grezza e
    // per i collaboratori NON contiene is_collaborator/active_shared_count, da cui
    // dipende il gate d'accesso. Fallback alla risposta login se /auth/me fallisce.
    setPianoScaduto(null);
    try {
      const me = await api.get("/auth/me");
      persistUser(me.data);
      return me.data;
    } catch {
      persistUser(data.user);
      return data.user;
    }
  }, [persistUser]);

  const login = useCallback(async (email, password, isAdminLogin = false) => {
    const endpoint = isAdminLogin ? "/auth/admin-login" : "/auth/login";
    const { data } = await api.post(endpoint, { email, password });
    // Secondo fattore attivo: il backend non ha consegnato nessuna sessione,
    // solo un gettone valido 5 minuti. Nessun token va salvato adesso.
    if (data?.totp_required) {
      return { totpRequired: true, pendingToken: data.pending_token, expiresIn: data.expires_in };
    }
    return applySession(data);
  }, [applySession]);

  /** Secondo passo del login admin: codice TOTP o codice di recupero. */
  const completeAdminTotp = useCallback(async (pendingToken, code) => {
    const { data } = await api.post("/auth/admin-login/2fa", {
      pending_token: pendingToken,
      code,
    });
    const utente = await applySession(data);
    return { user: utente, usedRecoveryCode: data.used_recovery_code, recoveryCodesLeft: data.recovery_codes_left };
  }, [applySession]);

  /** Applica una sessione ottenuta da un endpoint che restituisce access_token
   *  (es. /team/register, /team/accept): salva il token e carica l'utente
   *  canonico via /auth/me (con i flag collaboratore). */
  const applyInviteSession = useCallback(async () => {
    // I cookie li ha gia' impostati /team/accept o /team/register.
    const { data } = await api.get("/auth/me");
    persistUser(data);
    return data;
  }, [persistUser]);

  const register = useCallback(async (name, email, password, extra = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, ...extra });
    persistUser(data.user);
    return data.user;
  }, [persistUser]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] /auth/logout failed:", err?.message);
    }
    persistUser(false);
  }, [persistUser]);

  const refreshUser = useCallback(() => fetchMe(), [fetchMe]);

  const exitImpersonation = useCallback(async () => {
    // Il token dell'admin sta in un cookie httpOnly messo da parte al momento
    // dell'impersonazione: solo il backend puo' rimetterlo al suo posto.
    try {
      await api.post("/auth/exit-impersonation");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] uscita da impersonazione fallita:", err?.message);
    }
    localStorage.removeItem(USER_CACHE_KEY);
    window.location.href = ADMIN_PATH;
  }, []);

  // ── Trial / subscription computed state ────────────────────────
  useEffect(() => {
    const updateAccess = () => { fetchMe(); };
    window.addEventListener(ABBONAMENTO_RICHIESTO, updateAccess);
    return () => window.removeEventListener(ABBONAMENTO_RICHIESTO, updateAccess);
  }, [fetchMe]);
  // La regola sta in lib/accessoPiano.js perche' la legge anche la ripresa
  // della sessione, che decide prima del render se l'account e' ancora dentro.
  const trialSubscriptionState = useMemo(
    () => computeTrialSubscriptionState(user),
    [user],
  );

  // Lo dice il backend, non piu' la presenza di due token in localStorage:
  // get_current_user mette `impersonator_id` sul profilo quando il gettone
  // porta la rivendicazione `impersonator`.
  const isImpersonating = useMemo(() => {
    if (!user || typeof user !== "object") return false;
    return Boolean(user.impersonator_id);
  }, [user]);

  // Il rinnovo fallito e' l'unico modo in cui una sessione finisce senza che
  // l'utente abbia premuto logout: qui si chiude anche lato interfaccia.
  useEffect(() => {
    const scaduta = () => persistUser(false);
    window.addEventListener(SESSIONE_SCADUTA, scaduta);
    return () => window.removeEventListener(SESSIONE_SCADUTA, scaduta);
  }, [persistUser]);

  // Durante un'impersonazione il refresh nel cookie e' quello dell'admin e il
  // backend risponde 401 di proposito: senza questo flag ogni impersonazione
  // morirebbe al primo cambio di finestra, anche dopo cinque minuti.
  useEffect(() => {
    impostaImpersonazione(isImpersonating);
  }, [isImpersonating]);

  // Rinnovo quando la scheda torna in primo piano. Serve a immagini, PDF e
  // download, che partono da <img src> e da fetch e non passano
  // dall'interceptor di axios: con l'access token scaduto si romperebbero
  // senza nessuna rete di sicurezza.
  useEffect(() => {
    const tornata = () => {
      if (document.visibilityState !== "visible") return;
      if (!hasSessionHint()) return;
      rinnovaSeVecchio();
    };
    document.addEventListener("visibilitychange", tornata);
    return () => document.removeEventListener("visibilitychange", tornata);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, completeAdminTotp, register, logout, setUser: persistUser,
             refreshUser, applyInviteSession, isImpersonating, exitImpersonation, markTutorialSeen,
             pianoScaduto, ...trialSubscriptionState }),
    [user, loading, login, completeAdminTotp, register, logout, refreshUser, applyInviteSession,
     persistUser, isImpersonating, exitImpersonation, markTutorialSeen, trialSubscriptionState,
     pianoScaduto],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
